import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { sqWorkbookStore, sqWorksheetStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import { useHasAssetGroups } from '@/licenseManagement/licenseManagement.store';
import { navigateToTreeBuilderPage } from '@/main/routing.utilities';
import { SearchResult } from '@/search/SearchResult.molecule';
import { clearSelection, searchOrExplore } from '@/search/search.actions';
import { LIMIT_IN_OVERVIEW, SEARCH_RESULT_TYPES } from '@/search/search.constants';
import { useSearchActions, useSearchSelectedItemIds, useSearchSelectedItemTable } from '@/search/search.store';
import { canModifyWorkbook } from '@/services/authorization.service';
import { CreateModelButton } from '@/tools/models/CreateModelButton.molecule';
import { doTrack } from '@/track/track.service';
import { LOAD_STATUS } from '@/workbook/workbook.constants';
import { setDisplayAssetGroupEditor, setDisplayTableDefinitionEditor } from '@/worksheet/worksheet.actions';
import { Badge, Button, Icon } from '@seeqdev/qomponents';
import type { SearchModes, SearchPanes } from '@/search/search.constants';
import type { SelectedSearchItem } from '@/search/search.types';

export interface SearchGroupProps {
  loadStatus: keyof typeof LOAD_STATUS;
  label: string;
  mode: SearchModes;
  searchMode: SearchModes;
  searchTypes: string[];
  scopeIds: string[];
  restrictExploration: boolean;
  onErrorClick: () => void;
  onClickItem: (item: SelectedSearchItem) => void;
  items: any[];
  pane: SearchPanes;
  indicateSelection?: boolean;
  selectedItemId?: string;
  searchResultIcons: (item: SelectedSearchItem) => Promise<React.ReactNode>;
  noItemsMessage?: string;
  isSelectingAsset?: boolean;
  predefinedSearchTypes?: string[];
  totalItemsCount?: number;
  onSetModeIconClick?: () => void;
  showHomeIcon?: boolean;
  onHomeIconClick?: () => void;
  hideCreateButton?: boolean;
  showPreviewBadge?: boolean;
  showNewBadge?: boolean;
  disableSearchGroup?: boolean;
  tooltipLink?: string;
  onTooltipClicked?: () => void;
}

export const SearchGroup = ({
  items,
  loadStatus,
  label,
  mode,
  pane,
  onErrorClick,
  onClickItem,
  searchResultIcons,
  searchMode,
  searchTypes,
  scopeIds,
  restrictExploration,
  noItemsMessage,
  indicateSelection = false,
  selectedItemId,
  isSelectingAsset = false,
  predefinedSearchTypes,
  totalItemsCount = 0,
  onSetModeIconClick,
  showHomeIcon = false,
  onHomeIconClick,
  showPreviewBadge = false,
  hideCreateButton = false,
  showNewBadge = false,
  disableSearchGroup = false,
  tooltipLink,
  onTooltipClicked,
}: SearchGroupProps) => {
  const { t } = useTranslation();
  const displayAssetGroupEditor = useFluxPath(sqWorksheetStore, (store) => store.displayAssetGroupEditor);
  const displayScalingTableEditor = useFluxPath(sqWorksheetStore, (store) => store.displayTableDefinitionView);
  const workbookId = useFluxPath(sqWorkbookStore, (store) => store.workbookId);
  const hasAssetGroups = useHasAssetGroups();

  const selectedItemIds = useSearchSelectedItemIds();
  const selectedItemTable = useSearchSelectedItemTable();
  const { setMode } = useSearchActions();
  const showCreateAssetGroupButton =
    searchMode === 'asset-groups' && hasAssetGroups && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook);
  const showCreateScalingTableButton =
    !disableSearchGroup &&
    searchMode === 'scaling-tables' &&
    pane === 'main' &&
    canModifyWorkbook(sqWorkbookStore.workbook);
  const showCreateModelButton =
    searchMode === 'models' && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook) && !hideCreateButton;
  const showItemTableButton =
    searchMode === 'item-tables' && pane === 'main' && canModifyWorkbook(sqWorkbookStore.workbook) && !hideCreateButton;
  const displayHierarchyView = searchMode === 'item-hierarchies' && pane === 'main' && !!selectedItemTable;
  const showCreateHierarchyButton =
    displayHierarchyView && canModifyWorkbook(sqWorkbookStore.workbook) && !hideCreateButton;
  const searchingUsages = mode === 'usages';

  const setModeClick = () => {
    if (onSetModeIconClick) {
      onSetModeIconClick();
    } else {
      setMode(searchMode);
      if (searchMode !== 'usages') {
        searchOrExplore(pane, searchMode, searchTypes, restrictExploration, scopeIds);
      }
    }
  };

  const renderSetModeIconButton = (testId?: string) => {
    return (
      <Icon
        testId={testId ?? 'setModeIconButton'}
        icon="fa-chevron-right"
        extraClassNames="text-interactive pl5 pr5"
        onClick={() => setModeClick()}
      />
    );
  };

  const renderAssetGroupButton = (
    <div>
      <Button
        testId="createButton"
        label={t('SEARCH_DATA.CREATE')}
        onClick={() => {
          setDisplayAssetGroupEditor(true);
        }}
        size="xs"
        disabled={displayAssetGroupEditor}
        extraClassNames="tw:mr-1"
      />
      {renderSetModeIconButton('assetGroupMoreButton')}
    </div>
  );

  const renderScalingTableButton = (
    <div>
      <Button
        testId="scalingTableCreateButton"
        label={t('SEARCH_DATA.CREATE')}
        size="xs"
        onClick={() => {
          doTrack('Scaling Table Configuration', 'Open Create Item Finder Modal');
          setDisplayTableDefinitionEditor(true);
          resetItemFinder();
        }}
        disabled={displayScalingTableEditor}
        extraClassNames="tw:mr-1"
      />
      {renderSetModeIconButton('scalingMoreButton')}
    </div>
  );

  // Shared by the Item Table and Hierarchy create flows, which both open the Tree Builder page with
  // different navigation params and tracking details.
  const renderTreeBuilderCreateButton = ({
    testId,
    moreButtonTestId,
    extraClassNames,
    onCreateClick,
  }: {
    testId: string;
    moreButtonTestId: string;
    extraClassNames: string;
    onCreateClick: (event: React.MouseEvent | React.KeyboardEvent) => void;
  }) => (
    <div>
      <Button
        testId={testId}
        label={t('SEARCH_DATA.CREATE')}
        onClick={onCreateClick}
        size="xs"
        extraClassNames={extraClassNames}
      />
      {renderSetModeIconButton(moreButtonTestId)}
    </div>
  );

  const renderItemTableButton = renderTreeBuilderCreateButton({
    testId: 'itemTableCreateButton',
    moreButtonTestId: 'itemTablesMoreButton',
    extraClassNames: 'tw:mr-1',
    onCreateClick: (event) => {
      doTrack('Tree Builder', 'Create Button Clicked');
      navigateToTreeBuilderPage({ scopedTo: workbookId, event });
    },
  });

  const renderModelButton = (
    <div>
      <CreateModelButton />
      {renderSetModeIconButton('modelsMoreButton')}
    </div>
  );

  const renderHierarchyButton = renderTreeBuilderCreateButton({
    testId: 'hierarchyCreateButton',
    moreButtonTestId: 'hierarchyMoreButton',
    extraClassNames: 'pr5 mr5',
    onCreateClick: (event) => {
      doTrack('Tree Builder', 'Create Hierarchy', { itemTableId: selectedItemTable?.id });
      navigateToTreeBuilderPage({ tableId: selectedItemTable?.id, newHierarchy: true, event });
    },
  });

  const renderPreviewBadge = (
    <Badge testId="preview-badge" variant="preview" extraClassNames="ml7">
      {t('PREVIEW')}
    </Badge>
  );
  const renderNewBadge = (
    <Badge testId="new-badge" extraClassNames="ml7">
      {t('HOME_SCREEN.NEW')}
    </Badge>
  );

  const renderButton = () => {
    if (showCreateAssetGroupButton) {
      return renderAssetGroupButton;
    }
    if (showCreateScalingTableButton) {
      return renderScalingTableButton;
    }
    if (showItemTableButton) {
      return renderItemTableButton;
    }
    if (showCreateModelButton) {
      return renderModelButton;
    }
    if (showCreateHierarchyButton) {
      return renderHierarchyButton;
    }
    return renderSetModeIconButton();
  };

  const nonClickableHeading = (
    <span
      data-testid="nonClickableHeading"
      className="flexColumnContainer flexSpaceBetween flexAlignCenter heading text-interactive fs15">
      <span>
        <span data-testid="nonClickableHeadingLabel" className={disableSearchGroup ? 'disabledLook' : ''}>
          {t(`SEARCH_DATA.${label}`)}
          {showPreviewBadge && renderPreviewBadge}
          {showNewBadge && renderNewBadge}
        </span>
        {disableSearchGroup && (
          <span>
            {tooltipLink && (
              <a target="_blank" className="mt15" href={tooltipLink} onClick={onTooltipClicked} rel="noopener">
                <Icon icon="fa-question-circle" extraClassNames="fa-lg text-interactive ml5" testId="tooltipIcon" />
              </a>
            )}
          </span>
        )}
      </span>
      {showCreateAssetGroupButton && renderAssetGroupButton}
      {showCreateScalingTableButton && renderScalingTableButton}
      {showItemTableButton && renderItemTableButton}
      {showCreateModelButton && renderModelButton}
      {showCreateHierarchyButton && renderHierarchyButton}
    </span>
  );

  const loadingSpinner = (
    <div className="pt10 pb10 flexNoGrowNoShrink flexColumnContainer flexCenter">
      <IconWithSpinner spinning={true} large={true} />
      <span className="mb5 ml5 mt5">{t(`SEARCH_DATA.LOADING_${label.replace('.', '_')}`)}</span>
    </div>
  );

  // `loadStatus` is typed as "ERROR" | "LOADED" | "LOADING" | "NOT_LOADED" from keyof typeof LOAD_STATUS. But
  // `SEARCH_RESULT_TYPES.ASSETS` is typed as 'assets', so we need to use `as any` here.
  const isLoadingHierarchies = (loadStatus as any) === SEARCH_RESULT_TYPES.ASSETS && mode === 'item-hierarchies';

  return disableSearchGroup ? (
    <div data-testid="disabledStatus">{nonClickableHeading}</div>
  ) : (
    <div>
      {loadStatus === LOAD_STATUS.LOADING && !searchingUsages && (
        <div data-testid="loadingStatus">
          {nonClickableHeading}
          {loadingSpinner}
        </div>
      )}

      {loadStatus === LOAD_STATUS.ERROR && !searchingUsages && (
        <div data-testid="errorStatus">
          {nonClickableHeading}
          <div className="flexColumnContainer sq-text-danger cursorPointer pt3 pb6" onClick={onErrorClick}>
            <Icon icon="fa-exclamation-triangle" type="danger" extraClassNames="flexCenter pl5 pr10 pt2" />
            <div className="flexFill pl5">
              {t(`SEARCH_DATA.LOAD_${label.replace('.', '_')}_FAILED`)}
              <br />
              <span className="text-underline">{t('SEARCH_DATA.RETRY')}</span>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 &&
        ((mode === searchMode && !searchingUsages) || mode === 'overview') &&
        noItemsMessage &&
        pane === 'main' && (
          <div>
            {nonClickableHeading}
            {isLoadingHierarchies ? loadingSpinner : <div className="m5">{t(noItemsMessage)}</div>}
          </div>
        )}

      {items.length > 0 && (mode === searchMode || mode === 'overview') && (
        <div>
          {mode === 'overview' && (
            <div data-testid="modeSet" className="flexColumnContainer flexSpaceBetween flexAlignCenter heading">
              {showHomeIcon && (
                <div data-testid="homeIconContainer">
                  <Icon
                    icon="fa-house-chimney"
                    extraClassNames="pl5 pr2"
                    testId="breadcrumbHomeIcon"
                    onClick={onHomeIconClick}
                  />
                  <span className="pr2">»</span>
                </div>
              )}
              <span className="text-interactive flexFill fs15" onClick={() => setModeClick()}>
                {t(`SEARCH_DATA.${label}`)} {totalItemsCount > 0 && `(${totalItemsCount})`}
                {showPreviewBadge && renderPreviewBadge}
                {showNewBadge && renderNewBadge}
              </span>

              {renderButton()}
            </div>
          )}
          {mode === 'item-hierarchies' && displayHierarchyView && <>{nonClickableHeading}</>}

          {_.map(items, (item: any) => (
            <SearchResult
              isSelectingAsset={isSelectingAsset}
              searchTypes={predefinedSearchTypes}
              key={item.id}
              item={item}
              items={items}
              searchResultIcons={searchResultIcons}
              itemSelected={_.indexOf(selectedItemIds, item.id) > -1}
              onClickItem={() => {
                clearSelection(); // unselect selected items
                onClickItem(item);
              }}
              isSelected={indicateSelection && selectedItemId === item.id}
              noNavigate={restrictExploration}
            />
          ))}
        </div>
      )}
      {mode === 'overview' && totalItemsCount > LIMIT_IN_OVERVIEW && (
        <Button
          testId="viewMoreButton"
          label={t('SEARCH_DATA.VIEW_MORE')}
          onClick={() => setModeClick()}
          extraClassNames="viewMoreLinkButton"
        />
      )}
    </div>
  );
};
